<template>
  <div v-if="!isLoading" class="pt-32">
    <div v-if="hasAnyAdventures">
      <div class="max-w-352 m-auto w-full" style="min-height: calc(100vh - 530px)">
        <div class="flex flex-row justify-between mb-32">
          <h1 class="text-h1">Adventures</h1>
          <Button
            v-if="draftAdventure"
            class="-mr-8"
            value="Edit Draft"
            variant="text"
            size="small"
            to="/adventures/new/category"
          />
        </div>
        <div class="mb-48">
          <Draft v-if="draftAdventure" :draftAdventure="draftAdventure" />
          <div class="flex flex-row w-full justify-between">
            <h3 class="text-h3 mb-16">Active</h3>
            <Button
              v-if="!draftAdventure"
              class="-mr-8"
              value="Create New"
              variant="text"
              size="small"
              to="/adventures/new/category"
            />
          </div>
          <ul v-if="allActiveAdventures.length" class="flex flex-col">
            <li v-for="adventure in visibleActiveAdventures" :key="adventure.id">
              <ComplexCard
                class="mb-24"
                variant="active"
                :title="adventure.title"
                :firstLineText="firstLineText(adventure.adventureCategoryName, adventure.ageRangeText)"
                :headerImageUrl="adventure.adventureCategoryImage && adventure.adventureCategoryImage.url"
                :detailsUrl="adventureDetailsUrl(adventure.sitterLocation, adventure.id)"
              />
            </li>
          </ul>
          <Card v-else class="w-full">
            <h2 class="text-strong-body">No active Adventures</h2>
            <p class="text-small text-grey-text-subdued">
              Create a new Adventure or activate a previous one so parents can find it
            </p>
          </Card>
          <Button
            v-if="!showAllActive && allActiveAdventures.length > startingNumberVisible"
            variant="outlined"
            value="Show More"
            size="medium"
            class="w-full"
            @click="
              () => {
                showAllActive = true;
              }
            "
          />
        </div>
        <div v-if="allClosedAdventures.length" class="mb-48">
          <div class="flex flex-row w-full justify-between">
            <h3 class="text-h3 mb-16">Inactive</h3>
          </div>
          <ul class="flex flex-col">
            <li v-for="adventure in visibleClosedAdventures" :key="adventure.id">
              <ComplexCard
                class="mb-24"
                variant="inactive"
                :title="adventure.title"
                :firstLineText="firstLineText(adventure.adventureCategoryName, adventure.ageRangeText)"
                :headerImageUrl="adventure.adventureCategoryImage && adventure.adventureCategoryImage.url"
                :detailsUrl="adventureDetailsUrl(adventure.sitterLocation, adventure.id)"
              />
            </li>
          </ul>
          <Button
            v-if="!showAllClosed && allClosedAdventures.length > startingNumberVisible"
            variant="outlined"
            value="Show More"
            size="medium"
            class="w-full"
            @click="
              () => {
                showAllClosed = true;
              }
            "
          />
        </div>
      </div>
      <GetHelpfulInfo
        header="Be an Adventures expert 🤓"
        :articles="allActiveAdventures.length ? ['get', 'preparing', 'make'] : ['how', 'get', 'make']"
        style="padding-left: calc(50% - 182px)"
      />
    </div>

    <div v-else class="flex flex-row">
      <div class="flex z-[2] md:h-1120 lg:h-1144 md:basis-4/12 lg:basis-5/12 md:items-start md:justify-end md:pl-16">
        <CreateAdventureCTA :hasSitterProfile="hasSitterProfile" :hasDraft="!!draftAdventure" />
      </div>
      <div class="flex z-[1] w-full flex-col ml-16 md:ml-0 md:basis-8/12 lg:basis-7/12">
        <LeadAnAdventure :renderVideo="!draftAdventure" />
        <Draft v-if="draftAdventure" :draftAdventure="draftAdventure" />
        <WhatParentsSee />
        <CreateFirstAdventure />
        <GetHelpfulInfo header="Get helpful info 🤓" :articles="['how', 'what', 'safety']" />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import CreateAdventureCTA from './sections/CreateAdventureCTA.vue';
import LeadAnAdventure from './sections/LeadAnAdventure.vue';
import CreateFirstAdventure from './sections/CreateFirstAdventure.vue';
import WhatParentsSee from './sections/WhatParentsSee.vue';
import GetHelpfulInfo from './sections/GetHelpfulInfo.vue';
import Draft from './sections/Draft.vue';

import ComplexCard from '../../../components/complex_card/ComplexCard.vue';
import Button from '../../../components/button/Button.vue';
import Card from '../../../components/card/Card.vue';
import adventures from '@/lib/adventures';

import { SITTER_ADVENTURES_QUERY } from '../../../graphql/adventures/queries';
import { SITTER_PROFILE_QUERY } from '../../../graphql/account/provider/queries';

type AdventureCategoryImage = {
  url: string;
  label: string;
  adventureCategoryId: number;
};

interface SitterAdventure {
  adventureCategoryId: number;
  adventureCategoryImage: AdventureCategoryImage;
  adventureCategoryName: string;
  ageRangeText: string;
  id: number;
  maxPayRate: number;
  minPayRate: number;
  status: string;
  title: string;
}

export default {
  name: 'Dashboard',
  components: {
    ComplexCard,
    Button,
    Card,
    CreateAdventureCTA,
    CreateFirstAdventure,
    LeadAnAdventure,
    WhatParentsSee,
    GetHelpfulInfo,
    Draft,
  },
  data(): any {
    return {
      adventureStatus: this.$store.getters['adventures/adventureDraft'].status,
      sitterAdventures: [],
      showAllActive: false,
      showAllClosed: false,
      isLoading: true,
    };
  },
  computed: {
    hasAnyAdventures(): Boolean {
      return this.allActiveAdventures.length > 0 || this.visibleClosedAdventures.length > 0;
    },
    hasSitterProfile(): boolean {
      return this.sitterProfile?.babysitterProfileCompleted || false;
    },
    startingNumberVisible(): number {
      return 3;
    },
    allActiveAdventures(): Array<SitterAdventure> {
      return this.sitterAdventures.filter((adventure) => adventure.status === 'active');
    },
    allClosedAdventures(): Array<SitterAdventure> {
      return this.sitterAdventures.filter((adventure) => adventure.status === 'closed');
    },
    visibleActiveAdventures(): Array<SitterAdventure> {
      return this.allActiveAdventures.filter((_: void, i: number) => this.showAllActive || i < this.startingNumberVisible);
    },
    visibleClosedAdventures(): Array<SitterAdventure> {
      return this.allClosedAdventures.filter((_: void, i: number) => this.showAllClosed || i < this.startingNumberVisible);
    },
    draftAdventure(): SitterAdventure {
      return this.sitterAdventures.find((adventure) => adventure.status === 'draft');
    },
  },
  created() {
    this.$store.dispatch('adventures/resetStore');
  },
  mounted() {
    this.$apollo.queries.sitterAdventures.refetch();
  },
  methods: {
    adventureDetailsUrl(adventureLocation, adventureId): string | null {
      return adventures.getAdventureDetailsUrl(adventureLocation, adventureId);
    },
    firstLineText(adventureCategory, ageRange): string | null {
      return adventures.getFirstLineText('active', adventureCategory, ageRange);
    },
  },
  apollo: {
    sitterProfile: {
      query: SITTER_PROFILE_QUERY,
    },
    sitterAdventures: {
      query: SITTER_ADVENTURES_QUERY,
      variables: {
        status: ['active', 'closed', 'draft'],
      },
      result({ data, loading }) {
        if (!loading) {
          this.sitterAdventures = data.sitterAdventures?.edges?.map((edge) => edge.node?.adventure) || [];
          this.isLoading = false;
        }
      },
    },
  },
};
</script>
